import { useState } from "react";
import { Plus, Loader2, Globe, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const INTERVALS = [
  { value: "5", label: "5 minutes" },
  { value: "10", label: "10 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
];

export default function AddWebsiteDialog({ onAdded, trigger }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [interval, setInterval] = useState("5");
  const [type, setType] = useState("http");
  const [keyword, setKeyword] = useState("");
  const [keywordMatch, setKeywordMatch] = useState("present");
  const [tags, setTags] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setUrl(""); setName(""); setInterval("5"); setType("http");
    setKeyword(""); setKeywordMatch("present"); setTags("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/websites", {
        url,
        name: name || undefined,
        interval: parseInt(interval, 10),
        monitor_type: type,
        keyword: type === "keyword" ? keyword : undefined,
        keyword_match: keywordMatch,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success(`Now monitoring ${data.name}`);
      reset();
      setOpen(false);
      onAdded?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add webapp");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="add-website-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={16} className="mr-1.5" /> Add Webapp
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="add-website-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Webapp</DialogTitle>
          <DialogDescription>Your first check is queued automatically and runs on the next worker cycle.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          {/* Monitor type toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setType("http")}
              data-testid="type-http-btn"
              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                type === "http" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Globe size={15} /> HTTP(S)
            </button>
            <button
              type="button"
              onClick={() => setType("keyword")}
              data-testid="type-keyword-btn"
              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                type === "keyword" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <SearchIcon size={15} /> Keyword
            </button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="url" className="text-xs font-semibold">Webapp URL</Label>
            <Input
              id="url"
              data-testid="website-url-input"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              autoFocus
              className="font-mono focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {type === "keyword" && (
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="keyword" className="text-xs font-semibold">Keyword</Label>
                <Input
                  id="keyword"
                  data-testid="website-keyword-input"
                  placeholder="e.g. Sign in"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  required
                  className="focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Should be</Label>
                <Select value={keywordMatch} onValueChange={setKeywordMatch}>
                  <SelectTrigger data-testid="website-keyword-match"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs font-semibold">Display name (optional)</Label>
            <Input id="name" data-testid="website-name-input" placeholder="My Marketing Site" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tags" className="text-xs font-semibold">Tags (comma separated)</Label>
            <Input id="tags" data-testid="website-tags-input" placeholder="production, api" value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Monitoring interval</Label>
            <Select value={interval} onValueChange={setInterval}>
              <SelectTrigger data-testid="website-interval-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INTERVALS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="submit-webapp-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-1.5" />}
              Add Webapp
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
